// 201 -- full-screen notifications list. The mobile bell pushes here (stack
// nav + top-bar Back); desktop can reach it too but keeps its bell popover.
// Same useNotifications hook as the bell -- no new endpoint; the popover
// shows the 10 most recent, this page shows everything.
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { useNotifications, type Notification } from "@/hooks/useNotifications";
import { Button } from "@/components/ui/button";
import { NotificationItem, getNotificationPath } from "@/components/notification-item";

export default function NotificationsPage() {
  const { notifications, unreadCount, markAsRead, markAllRead, isLoading } = useNotifications();
  const navigate = useNavigate();

  function handleClick(notification: Notification) {
    if (!notification.read_at) markAsRead(notification.id);
    const path = getNotificationPath(notification);
    if (path) navigate(path);
  }

  return (
    <div
      className="flex flex-col gap-4"
      data-page="notifications"
      data-state={isLoading ? "loading" : "ready"}
    >
      <div className="flex items-center justify-between" data-region="header">
        <h1 className="text-2xl font-semibold">Notifications</h1>
        {unreadCount > 0 && (
          <Button variant="outline" size="sm" onClick={() => markAllRead()} data-action="mark-all-read">
            Mark all read
          </Button>
        )}
      </div>

      {isLoading && (
        <p className="py-8 text-center text-sm text-muted-foreground">Loading...</p>
      )}

      {!isLoading && notifications.length === 0 && (
        <div
          className="flex flex-col items-center gap-2 rounded-2xl border border-border bg-card p-8 text-center shadow-sm"
          data-region="empty"
        >
          <Bell className="h-6 w-6 text-muted-foreground" aria-hidden />
          <p className="text-sm text-muted-foreground">No notifications yet.</p>
        </div>
      )}

      {!isLoading && notifications.length > 0 && (
        <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm" data-region="list">
          {notifications.map((n) => (
            <NotificationItem key={n.id} notification={n} onClick={handleClick} />
          ))}
        </div>
      )}
    </div>
  );
}
